import { Controller } from '@hotwired/stimulus';

/**
 * Boots the DNSound drum machine on its control deck.
 *
 * Player and Sounds are imported lazily so the sample bank (and Howler) stay out of the initial
 * page load. Because the modules load over the network, the user may navigate away before they
 * resolve; the isConnected check drops the boot in that case instead of wiring a Player to a page
 * that is gone.
 *
 * Values:
 * - notes: JSON array of the zone's record types, e.g. '["soa","ns"]'
 * - domain: the zone's domain name
 * - sources: nested map of sample group -> name -> asset URL (see DnsoundHelper#dnsound_sources)
 * - recordsSelector: document-scoped selector for the rows the beat lights up
 */
export default class extends Controller {
  static values = {
    notes: String,
    domain: String,
    sources: Object,
    recordsSelector: { type: String, default: '.js-dnsound-records-table tbody tr' }
  };

  connect () {
    this.loadModules().then(([{ default: Player }, { default: Sounds }]) => {
      if (!this.element.isConnected) return;

      this.player = new Player(new Sounds(this.sourcesValue), this.element, this.recordsSelectorValue, this.notesValue, this.domainValue);
    });
  }

  /**
   * Loads the lazy modules through the page's delivery pipeline. A page that carries an import
   * map (the app subapp) resolves the pinned specifiers natively through it, so the modules
   * arrive as digested Propshaft assets; a page without one (site) falls back to Vite's dynamic
   * import, which resolves the same specifiers through the `components` alias and keeps its
   * code-split lazy chunk. The importmap branch feeds import() a variable: a string literal —
   * even with the vite-ignore comment — is rewritten by Vite's import analysis to a Vite URL,
   * which would silently put both branches on Vite. Temporary dual-pipeline scaffolding:
   * collapses to the plain imports when site cuts over to importmaps.
   *
   * @returns {Promise<Array<Object>>} The player and sounds modules.
   */
  loadModules () {
    if (document.querySelector('script[type="importmap"]'))
      return Promise.all(['components/dnsound/player', 'components/dnsound/sounds'].map(
        (specifier) => import(/* @vite-ignore */ specifier)
      ));

    return Promise.all([
      import('components/dnsound/player'),
      import('components/dnsound/sounds')
    ]);
  }

  disconnect () {
    if (this.player) this.player.stop();
  }
}
